import DOMPurify from "dompurify";
import { trackEvent, setCommonAttrs } from "@/utils/omega";
import { SOFI_SDK_VERSION, getBiotype } from "@/common/js/constant";
export const IsProd: boolean = process.env.NODE_ENV === "production";

/**
 * Detect social media in-app browsers
 * @returns {object} Browser detection result
 */
export const getBrowserEnv = () => {
  const ua = navigator.userAgent.toLowerCase();
  return {
    isWechat: /micromessenger/i.test(ua),
    isWhatsapp: /whatsapp/i.test(ua),
    isFacebook: /fban|fbav/i.test(ua),
    isTwitter: /twitter/i.test(ua),
    isInstagram: /instagram/i.test(ua),
    isMessenger: /fb_iab/i.test(ua) || /messenger/i.test(ua),
  };
};

/**
 * 获取cookie
 * @param sKey
 */
export const getCookieItem = (sKey: any) => {
  return (
    decodeURIComponent(
      document.cookie.replace(
        new RegExp(
          "(?:(?:^|.*;)\\s*" +
            encodeURIComponent(sKey).replace(/[-.+*]/g, "\\$&") +
            "\\s*\\=\\s*([^;]*).*$)|^.*$",
        ),
        "$1",
      ),
    ) || null
  );
};

/**
 *
 * @param fn debounce
 * @param wait
 */
export function debounce(fn: (e: any) => void, wait: number) {
  let timer: number | null = null;
  return (props?: any) => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      fn(props);
    }, wait);
  };
}

export function toDecimal(x: string) {
  var f = parseFloat(x);
  if (isNaN(f)) {
    return;
  }
  f = Math.round(Number(x) * 100) / 100;
  return f;
}

// format money 通用方法
export const renderMoney = (data: any, type: number = 0) => {
  switch (type) {
    // 金额 + 货币符号
    case 0:
      return data && data.valueDisplay + " " + data.currency;
    // 金额
    case 1:
      return data && data.valueDisplay;
    // 货币符号
    case 2:
      return data && data.currency;
  }
};

interface filterProps {
  name: string;
  value: number;
}

/**
 * formatList format Select 筛选项
 * @param data
 */
export const formatList = (data: any) => {
  const list: filterProps[] = [];
  Object.keys(data).forEach((item) => {
    list.push({
      name: data[item],
      value: Number(item),
    });
  });
  return list;
};

/**
 * formatList format Select 筛选项
 * @param data
 */
export const formatList2 = (data: any) => {
  const list: { name: string; value: number | string }[] = [];
  Object.keys(data).forEach((item) => {
    list.push({
      name: data[item],
      value: item,
    });
  });
  return list;
};

// format enums 通用方法
export const renderEnums = (data: any, enumsObj: any) => {
  return enumsObj[data];
};

// 去除对象里面的空值
export const repairParams = (params: any) => {
  const result = { ...params };
  const excludesParams = ["undefined", "null", "", "[]", "{}", "NaN"];
  Object.keys(params)?.forEach((key: string) => {
    if (excludesParams.includes(String(params[key]))) {
      Reflect.deleteProperty(result, key);
    }
  });
  return result;
};
// 高亮
export const highLight = (searchValue: string, defaultValue: string): string => {
  let result: string = searchValue.replace(
    new RegExp("(" + defaultValue + ")", "gi"),
    '<span class="highlight">$1</span>',
  );
  return result;
};

// 获取生物识别状态字段
// 0 代表未开通生物识别
// 1 代表开通了生物识别，且为强生物识别
// 2 代表开通了生物识别，且为弱生物识别
export const getBioFlag = (): Promise<number> => {
  return new Promise((resolve) => {
    const sdkVersion = SOFI_SDK_VERSION?.split(".");
    // 兼容低版本sdk
    if (Number(sdkVersion[0]) > 7 || (Number(sdkVersion[0]) === 7 && Number(sdkVersion[1]) >= 6)) {
      try {
        if (window.Fusion && window.Fusion.loadModule("SofiBridgeModule")) {
          window.Fusion.loadModule("SofiBridgeModule").sfGetBiometricsStatus((res: any) => {
            setCommonAttrs({ biology_type: getBiotype(res?.biologyType) });
            resolve(res?.status);
          });
        } else {
          trackEvent("tech_sofi_catch_error", {
            source: "utils.bridge.sfGetBiometricsStatus",
            data: "bridge not found",
          });
          resolve(0);
        }
      } catch (err) {
        trackEvent("tech_sofi_catch_error", {
          source: "utils.bridge.sfGetBiometricsStatus",
          data: JSON.stringify(err),
        });
        resolve(0);
      }
    } else {
      resolve(0);
    }
  });
};
// 版本号对比
export const compareVersion = (version1: string, version2: string) => {
  const v1 = version1.split(".").map(Number);
  const v2 = version2.split(".").map(Number);
  for (let i = 0; i < v1.length; i++) {
    if (v2[i] === undefined || v1[i] > v2[i]) {
      return 1;
    } else if (v1[i] < v2[i]) {
      return -1;
    }
  }
  return 0;
};

// 从URL参数中获取omegaId并设置到cookie中，过期时间为1年
export const setOidFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const omegaId = params.get("omegaId");
    if (!omegaId) return;
    document.cookie = `oid=${encodeURIComponent(omegaId)}; path=/; max-age=${60 * 60 * 24 * 365}`;
  } catch (err) {
    trackEvent("tech_sofi_catch_error", {
      source: "utils.setOidFromUrl",
      data: JSON.stringify(err),
    });
  }
}

// 高亮标记
export const highlightMarkerBlue = (value: string) => {
    const reg = /\{(.+?)\}/g; // {}及内部内容
    return DOMPurify.sanitize(value.replace(reg, (match, content) => `<span style='color: #3373FF;'>${content}</span>`));
}

